import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Users, ChevronDown, ChevronUp, Loader2, Trash2 } from "lucide-react";

const CATEGORY_LABELS = {
  podcast: "Podcast",
  drums: "Drums",
  band: "Band",
  dj: "DJ",
  vocals: "Vocals",
  instruments: "Instruments",
  other: "Other",
};

const SORT_LABELS = [
  { id: "popular",  label: "Popular" },
  { id: "trending", label: "Trending" },
  { id: "recent",   label: "Recent" },
];

/**
 * QuickArmCommunitySection — iter-208
 *
 * Expandable "Community Setups" strip that lives inside the Quick Arm
 * row. Collapsed by default so the row stays compact; tapping the
 * "Community" pill expands a scrollable lane of public arm-routing
 * templates with sort + category filters, like-hearts, and apply CTAs.
 *
 * Mirrors the iter-175 Suno Templates community section but slimmed
 * down to fit the Quick Arm row footprint (single horizontal scroll
 * lane rather than a full grid).
 */
export default function QuickArmCommunitySection({
  templates,
  loading,
  error,
  sort,
  setSort,
  category,
  setCategory,
  myUserId,    // string | null — used to decide which delete buttons to show
  onApply,     // (template) => void
  onToggleLike,// (id) => Promise<void>
  onDelete,    // (id) => Promise<void>
}) {
  const [expanded, setExpanded] = useState(false);
  const count = templates?.length || 0;

  return (
    <>
      <button
        type="button"
        data-testid="tracking-quick-arm-community-toggle"
        onClick={() => setExpanded((v) => !v)}
        className={`flex items-center gap-1 rounded-xl border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider transition ${
          expanded
            ? "border-fuchsia-400/50 bg-fuchsia-500/15 text-fuchsia-100"
            : "border-white/10 bg-white/[0.04] text-white/80 hover:border-fuchsia-400/40 hover:bg-fuchsia-500/[0.08] hover:text-fuchsia-100"
        }`}
        title="Discover arm-routing presets shared by the community"
      >
        <Users className="h-3 w-3" />
        Community
        {count > 0 && (
          <span className="rounded bg-fuchsia-400/30 px-1 text-[9px] font-bold tabular-nums text-white">
            {count}
          </span>
        )}
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            key="quick-arm-community-panel"
            data-testid="tracking-quick-arm-community-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="w-full overflow-hidden"
          >
            <div className="mt-2 space-y-2 rounded-xl border border-fuchsia-400/20 bg-fuchsia-500/[0.04] p-3">
              {/* Filter row */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/45">
                  Sort
                </span>
                {SORT_LABELS.map((s) => (
                  <button
                    key={`sort-${s.id}`}
                    type="button"
                    data-testid={`tracking-quick-arm-community-sort-${s.id}`}
                    onClick={() => setSort(s.id)}
                    className={`rounded-md px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider transition ${
                      sort === s.id
                        ? "bg-fuchsia-500/40 text-white ring-1 ring-fuchsia-300/60"
                        : "bg-white/[0.04] text-white/65 hover:bg-white/[0.10]"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
                <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.18em] text-white/45">
                  Category
                </span>
                <button
                  type="button"
                  data-testid="tracking-quick-arm-community-cat-all"
                  onClick={() => setCategory(null)}
                  className={`rounded-md px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider transition ${
                    !category
                      ? "bg-cyan-500/35 text-white ring-1 ring-cyan-300/55"
                      : "bg-white/[0.04] text-white/65 hover:bg-white/[0.10]"
                  }`}
                >
                  All
                </button>
                {Object.entries(CATEGORY_LABELS).map(([id, label]) => (
                  <button
                    key={`cat-${id}`}
                    type="button"
                    data-testid={`tracking-quick-arm-community-cat-${id}`}
                    onClick={() => setCategory(id === category ? null : id)}
                    className={`rounded-md px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider transition ${
                      category === id
                        ? "bg-cyan-500/35 text-white ring-1 ring-cyan-300/55"
                        : "bg-white/[0.04] text-white/65 hover:bg-white/[0.10]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Content row */}
              {loading && (
                <div className="flex items-center gap-2 py-4 text-center text-xs text-white/55">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading community presets…
                </div>
              )}
              {!loading && error && (
                <div
                  data-testid="tracking-quick-arm-community-error"
                  className="rounded-lg border border-rose-400/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-100"
                >
                  Couldn’t load community presets: {error}
                </div>
              )}
              {!loading && !error && count === 0 && (
                <div className="rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-6 text-center text-xs text-white/55">
                  No community presets yet — be the first to share one with the
                  &ldquo;+ Save current&rdquo; pill above.
                </div>
              )}
              {!loading && !error && count > 0 && (
                <ul
                  data-testid="tracking-quick-arm-community-list"
                  className="flex flex-wrap gap-2"
                >
                  {templates.map((t) => (
                    <li
                      key={`community-${t.id}`}
                      data-testid={`tracking-quick-arm-community-${t.id}`}
                      className="group flex max-w-[260px] items-center gap-1 rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/[0.08] text-fuchsia-100 transition hover:border-fuchsia-300/60"
                    >
                      <button
                        type="button"
                        data-testid={`tracking-quick-arm-community-apply-${t.id}`}
                        onClick={() => onApply?.(t)}
                        className="flex items-center gap-1 rounded-l-xl py-1 pl-2.5 pr-2 text-left"
                        title={`Apply "${t.title}" — ${t.channels?.length || 0} channel${t.channels?.length === 1 ? "" : "s"} from ${t.creator_name}`}
                      >
                        <span className="rounded bg-fuchsia-400/30 px-1 font-mono text-[9px] font-bold tabular-nums text-white">
                          {t.channels?.length || 0}
                        </span>
                        <span className="flex flex-col items-start font-mono text-[10px] uppercase tracking-wider leading-tight">
                          <span className="max-w-[170px] truncate">{t.title}</span>
                          <span className="text-[8px] text-fuchsia-200/65">
                            {t.creator_name} · {CATEGORY_LABELS[t.category] || t.category}
                          </span>
                        </span>
                      </button>
                      <button
                        type="button"
                        data-testid={`tracking-quick-arm-community-like-${t.id}`}
                        onClick={(e) => { e.stopPropagation(); onToggleLike?.(t.id); }}
                        className={`flex items-center gap-0.5 px-1.5 py-1 font-mono text-[9px] uppercase tracking-wider transition ${
                          t.liked_by_me
                            ? "text-rose-200"
                            : "text-fuchsia-200/65 hover:text-rose-200"
                        }`}
                        title={t.liked_by_me ? "Unlike" : "Like"}
                      >
                        <Heart
                          className={`h-3 w-3 ${t.liked_by_me ? "fill-current" : ""}`}
                        />
                        {t.like_count > 0 ? t.like_count : ""}
                      </button>
                      {t.is_mine && /* iter-208 follow-up: only the user's own templates */
                        (
                          <button
                            type="button"
                            data-testid={`tracking-quick-arm-community-delete-${t.id}`}
                            onClick={(e) => { e.stopPropagation(); onDelete?.(t.id, t.title); }}
                            className="grid h-7 w-7 place-items-center rounded-r-xl text-fuchsia-200/40 opacity-0 transition group-hover:opacity-100 hover:bg-rose-500/30 hover:text-rose-100"
                            title="Delete (creators only)"
                            aria-label={`Delete community preset ${t.title}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
